import {
  clearAdsAttributionCookie,
  getAdsAttributionCookie,
  setAdsAttributionCookie,
} from "./adsAttributionCookie";
import { installAdsRequestSanitization } from "./adsRequestSanitization";
import {
  getRedditAdsClickId,
  isRedditAdsRolloutCountry,
  REDDIT_ADS_CLICK_ID_QUERY_PARAM,
  REDDIT_ADS_CLICK_ID_STORAGE_KEY,
} from "./redditAdsClickId";

const REDDIT_PIXEL_SCRIPT_ID = "reddit-ads-pixel";
const REDDIT_PIXEL_SCRIPT_URL = "https://www.redditstatic.com/ads/pixel.js";
const REDDIT_MEASUREMENT_READY_TIMEOUT_MS = 1500;
const REDDIT_SCRIPT_LOAD_TIMEOUT_MS = 1500;
const REDDIT_REQUEST_SANITIZATION_MARKER = "__tapestryRedditAdsRequestSanitizationInstalled";
const REDDIT_ACCOUNT_CREATION_EVENT = "AddToWishlist";
const RESTRICTED_PAGE_URL = "https://ads.openai.com/";
const REDDIT_CLICK_ID_COOKIE_NAME = "_rdt_cid";
const REDDIT_CLICK_ID_COOKIE_MAX_AGE_DAYS = 90;
const REDDIT_PENDING_CONVERSIONS_STORAGE_KEY =
  "tapestry:redditAds:pendingAccountCreationConversions";
const REDDIT_PENDING_CLICK_IDS_STORAGE_KEY =
  "tapestry:redditAds:pendingAccountCreationConversionClickIds";
const REDDIT_TRACKED_CONVERSIONS_STORAGE_KEY =
  "tapestry:redditAds:trackedAccountCreationConversions";
const MAX_STORED_TRANSACTION_IDS = 20;

type Rdt = ((...args: unknown[]) => void) & {
  callQueue?: IArguments[];
  sendEvent?: (...args: unknown[]) => void;
};

declare global {
  interface Window {
    rdt?: Rdt;
  }
}

let measurementEnabled = false;
let consentResolved = true;
let pixelId: string | undefined;
let shareAllConversions = false;
let measurementReadyWaiters: Array<(ready: boolean) => void> = [];
let scriptLoaded = false;
let scriptLoadPromise: Promise<boolean> | undefined;
let initializedPixelId: string | undefined;
const inFlightTransactionIds = new Set<string>();
const trackedTransactionIds = new Set<string>();

export { isRedditAdsRolloutCountry };

export function setRedditAdsMeasurementState({
  enabled,
  isConsentResolved,
  shouldClearAttribution = false,
  shouldShareAllConversions = false,
  configuredPixelId,
}: {
  enabled: boolean;
  isConsentResolved: boolean;
  shouldClearAttribution?: boolean;
  shouldShareAllConversions?: boolean;
  configuredPixelId: string;
}) {
  measurementEnabled = enabled;
  consentResolved = isConsentResolved;
  shareAllConversions = shouldShareAllConversions;
  pixelId = configuredPixelId || undefined;

  if (measurementEnabled && typeof window !== "undefined") {
    captureRedditAdsClickIdFromCurrentUrl();
    void drainPendingRedditAdsConversions();
  } else if (typeof window !== "undefined" && shouldClearAttribution) {
    clearStoredRedditAdsClickId();
    clearAdsAttributionCookie(REDDIT_CLICK_ID_COOKIE_NAME);
    clearPendingRedditAdsConversions();
    removeRedditAdsClickIdFromCurrentUrl();
    if (window.rdt) {
      if (initializedPixelId) {
        try {
          window.rdt("disableFirstPartyCookies");
        } catch (error) {
          console.warn("Failed to disable Reddit Ads first-party cookies", { error });
        }
        initializedPixelId = undefined;
      }
      if (!scriptLoaded && window.rdt.callQueue) {
        window.rdt.callQueue = [];
      }
    }
  }
  notifyMeasurementStateChanged();
}

export async function trackRedditAdsAccountCreated(transactionId: string): Promise<void> {
  if (!transactionId || hasTrackedTransactionId(transactionId)) return;
  let clickId = getStoredRedditAdsClickId();
  if (!isMeasurementReady()) {
    if (!(await waitForMeasurementReady())) return;
    clickId = getStoredRedditAdsClickId();
  }
  if (!clickId && !shareAllConversions) return;

  // Persist synchronously so an immediate post-create navigation cannot lose the conversion.
  storePendingTransactionId(transactionId, clickId);
  if (!isMeasurementReady() && !(await waitForMeasurementReady())) return;
  if (!pixelId) return;
  await drainPendingRedditAdsConversions();
}

async function drainPendingRedditAdsConversions(): Promise<void> {
  if (!isMeasurementReady()) return;
  const targetPixelId = pixelId;
  if (!targetPixelId) return;

  const transactionIds = getStoredTransactionIds(REDDIT_PENDING_CONVERSIONS_STORAGE_KEY);
  await Promise.all(
    transactionIds.map((transactionId) => {
      const clickId = getPendingClickId(transactionId, getStoredRedditAdsClickId());
      if (!clickId && !shareAllConversions) return Promise.resolve();
      return deliverRedditAdsConversion({ transactionId, clickId, targetPixelId });
    }),
  );
}

async function deliverRedditAdsConversion({
  transactionId,
  clickId,
  targetPixelId,
}: {
  transactionId: string;
  clickId: string | undefined;
  targetPixelId: string;
}): Promise<void> {
  if (hasTrackedTransactionId(transactionId) || inFlightTransactionIds.has(transactionId)) return;
  inFlightTransactionIds.add(transactionId);
  try {
    installAdsRequestSanitization({
      marker: REDDIT_REQUEST_SANITIZATION_MARKER,
      rewriteUrl: rewriteRedditAdsRequestUrl,
    });
    if (clickId) {
      ensureRedditAttributionCookie(clickId);
    } else {
      clearAdsAttributionCookie(REDDIT_CLICK_ID_COOKIE_NAME);
    }
    const rdt = getOrCreateRdt();

    if (initializedPixelId !== targetPixelId) {
      rdt("init", targetPixelId, {
        dpm: "LDU",
        dpcc: "ALL",
        dprc: "ALL",
      });
      initializedPixelId = targetPixelId;
    }
    if (
      !(await loadRedditPixelScript()) ||
      !measurementEnabled ||
      (!clickId && !shareAllConversions) ||
      pixelId !== targetPixelId
    )
      return;

    const loadedRdt = window.rdt ?? rdt;
    loadedRdt("track", REDDIT_ACCOUNT_CREATION_EVENT, {
      ...(clickId ? { rdt_cid: clickId } : {}),
      conversionId: transactionId,
    });
    restoreCurrentRedditAttributionCookie(clickId);
    markTransactionIdTracked(transactionId);
  } catch (error) {
    console.warn("Failed to track Reddit Ads account creation conversion", { error });
  } finally {
    inFlightTransactionIds.delete(transactionId);
  }
}

function getStoredTransactionIds(storageKey: string): string[] {
  try {
    const value = JSON.parse(window.sessionStorage.getItem(storageKey) ?? "[]");
    if (!Array.isArray(value)) return [];
    return value.filter((item): item is string => typeof item === "string" && item.length > 0);
  } catch {
    return [];
  }
}

function setStoredTransactionIds(storageKey: string, transactionIds: string[]) {
  try {
    window.sessionStorage.setItem(
      storageKey,
      JSON.stringify(transactionIds.slice(-MAX_STORED_TRANSACTION_IDS)),
    );
  } catch {
    // Measurement storage failures must not block account creation.
  }
}

function storePendingTransactionId(transactionId: string, clickId: string | undefined) {
  const pending = getStoredTransactionIds(REDDIT_PENDING_CONVERSIONS_STORAGE_KEY);
  if (pending.includes(transactionId)) return;

  const nextPending = [...pending, transactionId].slice(-MAX_STORED_TRANSACTION_IDS);
  setStoredTransactionIds(REDDIT_PENDING_CONVERSIONS_STORAGE_KEY, nextPending);
  setPendingClickId(transactionId, clickId, nextPending);
}

function getPendingClickIds(): Record<string, string | null> {
  try {
    const value = JSON.parse(
      window.sessionStorage.getItem(REDDIT_PENDING_CLICK_IDS_STORAGE_KEY) ?? "{}",
    );
    if (!value || typeof value !== "object" || Array.isArray(value)) return {};
    return Object.fromEntries(
      Object.entries(value).filter(
        (entry): entry is [string, string | null] =>
          typeof entry[0] === "string" && (typeof entry[1] === "string" || entry[1] === null),
      ),
    );
  } catch {
    return {};
  }
}

function setPendingClickId(
  transactionId: string,
  clickId: string | undefined,
  pendingTransactionIds: string[],
) {
  try {
    const pendingClickIds = Object.fromEntries(
      Object.entries(getPendingClickIds()).filter(([pendingId]) =>
        pendingTransactionIds.includes(pendingId),
      ),
    );
    pendingClickIds[transactionId] = clickId ?? null;
    window.sessionStorage.setItem(
      REDDIT_PENDING_CLICK_IDS_STORAGE_KEY,
      JSON.stringify(pendingClickIds),
    );
  } catch {
    // Measurement storage failures must not block account creation.
  }
}

function getPendingClickId(
  transactionId: string,
  legacyClickId: string | undefined,
): string | undefined {
  const pendingClickIds = getPendingClickIds();
  return Object.prototype.hasOwnProperty.call(pendingClickIds, transactionId) ?
      pendingClickIds[transactionId] ?? undefined
    : legacyClickId;
}

function hasTrackedTransactionId(transactionId: string): boolean {
  return (
    trackedTransactionIds.has(transactionId) ||
    getStoredTransactionIds(REDDIT_TRACKED_CONVERSIONS_STORAGE_KEY).includes(transactionId)
  );
}

function markTransactionIdTracked(transactionId: string) {
  trackedTransactionIds.add(transactionId);
  const tracked = getStoredTransactionIds(REDDIT_TRACKED_CONVERSIONS_STORAGE_KEY);
  if (!tracked.includes(transactionId)) {
    setStoredTransactionIds(REDDIT_TRACKED_CONVERSIONS_STORAGE_KEY, [...tracked, transactionId]);
  }
  setStoredTransactionIds(
    REDDIT_PENDING_CONVERSIONS_STORAGE_KEY,
    getStoredTransactionIds(REDDIT_PENDING_CONVERSIONS_STORAGE_KEY).filter(
      (pendingId) => pendingId !== transactionId,
    ),
  );
  const pendingClickIds = getPendingClickIds();
  delete pendingClickIds[transactionId];
  try {
    window.sessionStorage.setItem(
      REDDIT_PENDING_CLICK_IDS_STORAGE_KEY,
      JSON.stringify(pendingClickIds),
    );
  } catch {
    // Measurement storage failures must not block account creation.
  }
}

function clearPendingRedditAdsConversions() {
  try {
    window.sessionStorage.removeItem(REDDIT_PENDING_CONVERSIONS_STORAGE_KEY);
    window.sessionStorage.removeItem(REDDIT_PENDING_CLICK_IDS_STORAGE_KEY);
  } catch {
    // Consent revocation must not fail when storage is unavailable.
  }
  inFlightTransactionIds.clear();
}

export function captureRedditAdsClickIdFromCurrentUrl() {
  if (!measurementEnabled || typeof window === "undefined") return;

  const searchParams = new URLSearchParams(window.location.search);
  if (!searchParams.has(REDDIT_ADS_CLICK_ID_QUERY_PARAM)) return;

  const clickId = getRedditAdsClickId(searchParams);
  if (clickId) {
    try {
      window.sessionStorage.setItem(REDDIT_ADS_CLICK_ID_STORAGE_KEY, clickId);
    } catch {
      // The first-party attribution cookie remains available if session storage is unavailable.
    }
    setRedditAttributionCookie(clickId);
  } else {
    try {
      window.sessionStorage.removeItem(REDDIT_ADS_CLICK_ID_STORAGE_KEY);
    } catch {
      // Invalid attribution still clears the first-party cookie below.
    }
    clearAdsAttributionCookie(REDDIT_CLICK_ID_COOKIE_NAME);
  }
  removeRedditAdsClickIdFromCurrentUrl();
}

export function rewriteRedditAdsRequestUrl(rawUrl: string): string;
export function rewriteRedditAdsRequestUrl(rawUrl: URL): URL;
export function rewriteRedditAdsRequestUrl(rawUrl: string | URL): string | URL;
export function rewriteRedditAdsRequestUrl(rawUrl: string | URL) {
  try {
    const url = new URL(rawUrl.toString(), window.location.href);
    if (!isRedditPixelRequest(url) || !url.searchParams.has("esurl")) return rawUrl;

    url.searchParams.set("esurl", RESTRICTED_PAGE_URL);
    return rawUrl instanceof URL ? url : url.toString();
  } catch {
    return rawUrl;
  }
}

function isRedditPixelRequest(url: URL): boolean {
  return (
    url.hostname === "reddit.com" ||
    url.hostname.endsWith(".reddit.com") ||
    url.hostname === "redditmedia.com" ||
    url.hostname.endsWith(".redditmedia.com")
  );
}

function getStoredRedditAdsClickId(): string | undefined {
  try {
    const storedClickId = window.sessionStorage.getItem(REDDIT_ADS_CLICK_ID_STORAGE_KEY);
    if (storedClickId) {
      const validatedClickId = getRedditAdsClickId(
        new URLSearchParams([[REDDIT_ADS_CLICK_ID_QUERY_PARAM, storedClickId]]),
      );
      if (validatedClickId) return validatedClickId;
    }
  } catch {
    // Fall back to Reddit's first-party attribution cookie.
  }

  const cookieClickId = getAdsAttributionCookie(REDDIT_CLICK_ID_COOKIE_NAME);
  return cookieClickId ?
      getRedditAdsClickId(new URLSearchParams([[REDDIT_ADS_CLICK_ID_QUERY_PARAM, cookieClickId]]))
    : undefined;
}

function clearStoredRedditAdsClickId() {
  try {
    window.sessionStorage.removeItem(REDDIT_ADS_CLICK_ID_STORAGE_KEY);
  } catch {
    // Consent revocation must not fail when storage is unavailable.
  }
}

function setRedditAttributionCookie(clickId: string) {
  setAdsAttributionCookie({
    name: REDDIT_CLICK_ID_COOKIE_NAME,
    value: clickId,
    maxAgeDays: REDDIT_CLICK_ID_COOKIE_MAX_AGE_DAYS,
  });
}

function ensureRedditAttributionCookie(clickId: string) {
  if (getAdsAttributionCookie(REDDIT_CLICK_ID_COOKIE_NAME) === clickId) return;
  setRedditAttributionCookie(clickId);
}

function restoreCurrentRedditAttributionCookie(deliveredClickId: string | undefined) {
  const currentClickId = getStoredRedditAdsClickId();
  if (currentClickId && currentClickId !== deliveredClickId) {
    setRedditAttributionCookie(currentClickId);
  }
}

function removeRedditAdsClickIdFromCurrentUrl() {
  try {
    const url = new URL(window.location.href);
    url.searchParams.delete(REDDIT_ADS_CLICK_ID_QUERY_PARAM);
    window.history.replaceState(
      window.history.state,
      "",
      `${url.pathname}${url.search}${url.hash}`,
    );
  } catch {
    // URL cleanup must not block conversion tracking.
  }
}

function getOrCreateRdt(): Rdt {
  if (window.rdt) return window.rdt;

  const rdt = function redditPixelQueue() {
    // Match Reddit's bootstrap queue shape so pixel.js can process commands.
    // eslint-disable-next-line prefer-rest-params
    rdt.sendEvent ? rdt.sendEvent(...Array.from(arguments)) : rdt.callQueue?.push(arguments);
  } as Rdt;
  rdt.callQueue = [];
  window.rdt = rdt;
  return rdt;
}

function loadRedditPixelScript(): Promise<boolean> {
  if (scriptLoaded) return Promise.resolve(true);
  if (scriptLoadPromise) return scriptLoadPromise;

  const existingScript = document.getElementById(REDDIT_PIXEL_SCRIPT_ID);
  if (existingScript instanceof HTMLScriptElement) {
    scriptLoadPromise = waitForScriptLoad(existingScript);
    return scriptLoadPromise;
  }

  const script = document.createElement("script");
  script.id = REDDIT_PIXEL_SCRIPT_ID;
  script.async = true;
  script.src = REDDIT_PIXEL_SCRIPT_URL;
  scriptLoadPromise = waitForScriptLoad(script);
  document.head.appendChild(script);
  return scriptLoadPromise;
}

function waitForScriptLoad(script: HTMLScriptElement): Promise<boolean> {
  return new Promise((resolve) => {
    let settled = false;
    const finish = (loaded: boolean) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      resolve(loaded);
    };
    const timeoutId = window.setTimeout(() => {
      scriptLoadPromise = undefined;
      finish(false);
    }, REDDIT_SCRIPT_LOAD_TIMEOUT_MS);
    script.addEventListener(
      "load",
      () => {
        scriptLoaded = true;
        scriptLoadPromise = Promise.resolve(true);
        finish(true);
        void drainPendingRedditAdsConversions();
      },
      { once: true },
    );
    script.addEventListener(
      "error",
      () => {
        scriptLoadPromise = undefined;
        initializedPixelId = undefined;
        if (window.rdt?.callQueue) {
          window.rdt.callQueue = [];
        }
        script.remove();
        finish(false);
      },
      { once: true },
    );
  });
}

function isMeasurementReady(): boolean {
  return measurementEnabled && typeof window !== "undefined" && pixelId != null;
}

function shouldStopWaitingForMeasurementReady(): boolean {
  return consentResolved && !measurementEnabled;
}

function waitForMeasurementReady(): Promise<boolean> {
  if (isMeasurementReady()) return Promise.resolve(true);
  if (typeof window === "undefined" || shouldStopWaitingForMeasurementReady()) {
    return Promise.resolve(false);
  }

  return new Promise((resolve) => {
    let settled = false;
    const finish = (ready: boolean) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      measurementReadyWaiters = measurementReadyWaiters.filter((waiter) => waiter !== finish);
      resolve(ready);
    };
    const timeoutId = window.setTimeout(() => finish(false), REDDIT_MEASUREMENT_READY_TIMEOUT_MS);
    measurementReadyWaiters.push(finish);
  });
}

function notifyMeasurementStateChanged() {
  if (!isMeasurementReady() && !shouldStopWaitingForMeasurementReady()) return;

  const ready = isMeasurementReady();
  const waiters = measurementReadyWaiters;
  measurementReadyWaiters = [];
  for (const waiter of waiters) waiter(ready);
}
